import { useEffect, useRef, useState } from "react";
import { usePageScroll } from "../../../domains/app/hooks/usePageScroll";

/** How long the scroll must rest before its position is stored. */
const SCROLL_REST_MS = 150;

/**
 * The scroll of a desktop collection: whether the content has left the top
 * (the top bar floats over the banner until it does), and the position to come
 * back to, which the page restores on its next mount at the same path, before
 * paint.
 *
 * The position is stored once the scroll rests, so any way out comes back to
 * where the reader left, without a store write on every frame. A press or a
 * key inside the page stores a pending position at once (`flush`, bound in the
 * capture phase), so a link followed right after a scroll (an article, a
 * video, a journal, another collection) keeps it too. It cannot wait for the
 * unmount: the store is keyed by the current path, which by then is the next
 * page's.
 */
export function useCollectionScroll(): {
  scrollRef: React.RefObject<HTMLDivElement>;
  isScrolled: boolean;
  onScroll: (event: React.UIEvent<HTMLElement>) => void;
  flush: () => void;
} {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pending = useRef<{
    top: number;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { setPageScrollY } = usePageScroll(scrollRef);

  function cancelPending(): void {
    if (pending.current === null) return;
    clearTimeout(pending.current.timer);
    pending.current = null;
  }

  // A pending write must not land after the page has gone, under the next
  // page's path.
  useEffect(() => cancelPending, []);

  return {
    scrollRef,
    isScrolled,
    onScroll: (event) => {
      const top = event.currentTarget.scrollTop;
      setIsScrolled(top > 0);
      cancelPending();
      pending.current = {
        top,
        timer: setTimeout(() => {
          pending.current = null;
          setPageScrollY(top);
        }, SCROLL_REST_MS),
      };
    },
    flush: () => {
      if (pending.current === null) return;
      const top = pending.current.top;
      cancelPending();
      setPageScrollY(top);
    },
  };
}
